/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { FileType, IContentUri, IHttpOpts, QueryDict, Upload, UploadOpts, UploadResponse } from './interface';

import { removeElement } from '../utils';
import * as callbacks from '../utils/realtime-callbacks';
import { ConnectionError } from './error';
import { FetchHttpApi } from './fetch';
import { Method } from './method';
import { MediaPrefix } from './prefix';
import { parseErrorResponse } from './utils';

export * from './error';
export * from './interface';
export * from './method';
export * from './prefix';
export * from './utils';

export class XHubChatHttpApi<O extends IHttpOpts> extends FetchHttpApi<O> {
  private uploads: Upload[] = [];

  /**
   * Upload content to the server
   *
   * @param file - The object to upload. On a browser, something that
   *   can be sent to XMLHttpRequest.send (typically a File).  Under node.js,
   *   a Buffer, String or ReadStream.
   *
   * @param opts - options object
   *
   * @returns Promise which resolves to response object, or rejects with an error (usually a XHubChatError).
   */
  public uploadContent(file: FileType, opts: UploadOpts = {}): Promise<UploadResponse> {
    const includeFilename = opts.includeFilename ?? true;
    const abortController = opts.abortController ?? new AbortController();

    // If the file doesn't have a mime type, use a default since the HS errors if we don't supply one.
    const contentType = (opts.type ?? (file as File).type) || 'application/octet-stream';
    const fileName = opts.name ?? (file as File).name;

    const upload = {
      loaded: 0,
      total: 0,
      abortController,
    } as Upload;
    const uploadResolvers = Promise.withResolvers<UploadResponse>();

    if (globalThis.XMLHttpRequest) {
      const xhr = new globalThis.XMLHttpRequest();

      const timeoutFn = function (): void {
        xhr.abort();
        uploadResolvers.reject(new Error('Timeout'));
      };

      // set an initial timeout of 30s; we'll advance it each time we get a progress notification
      let timeoutTimer = callbacks.setTimeout(timeoutFn, 30000);

      xhr.onreadystatechange = function (): void {
        switch (xhr.readyState) {
          case globalThis.XMLHttpRequest.DONE:
            callbacks.clearTimeout(timeoutTimer);
            try {
              if (xhr.status === 0) {
                throw new DOMException(xhr.statusText, 'AbortError'); // mimic fetch API
              }
              if (!xhr.responseText) {
                throw new Error('No response body.');
              }

              if (xhr.status >= 400) {
                uploadResolvers.reject(parseErrorResponse(xhr, xhr.responseText));
              } else {
                uploadResolvers.resolve(JSON.parse(xhr.responseText));
              }
            } catch (err) {
              if ((<Error>err).name === 'AbortError') {
                uploadResolvers.reject(err);
                return;
              }
              uploadResolvers.reject(new ConnectionError('request failed', <Error>err));
            }
            break;
        }
      };

      xhr.upload.onprogress = (ev: ProgressEvent): void => {
        callbacks.clearTimeout(timeoutTimer);
        upload.loaded = ev.loaded;
        upload.total = ev.total;
        timeoutTimer = callbacks.setTimeout(timeoutFn, 30000);
        opts.progressHandler?.({
          loaded: ev.loaded,
          total: ev.total,
        });
      };

      const url = this.getUrl('/upload', undefined, MediaPrefix.V1);

      if (includeFilename && fileName) {
        url.searchParams.set('filename', encodeURIComponent(fileName));
      }

      if (!this.opts.useAuthorizationHeader && this.opts.accessToken) {
        url.searchParams.set('access_token', encodeURIComponent(this.opts.accessToken));
      }

      xhr.open(Method.Post, url.href);
      if (this.opts.useAuthorizationHeader && this.opts.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.opts.accessToken}`);
      }
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);

      abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });
    } else {
      const queryParams: QueryDict = {};
      if (includeFilename && fileName) {
        queryParams.filename = fileName;
      }

      const headers: Record<string, string> = { 'Content-Type': contentType };

      this.authedRequest<UploadResponse>(Method.Post, '/upload', queryParams, file, {
        prefix: MediaPrefix.V1,
        headers,
        abortSignal: abortController.signal,
      }).then(uploadResolvers.resolve, uploadResolvers.reject);
    }

    // remove the upload from the list on completion
    upload.promise = uploadResolvers.promise.finally(() => {
      removeElement(this.uploads, elem => elem === upload);
    });
    abortController.signal.addEventListener('abort', () => {
      removeElement(this.uploads, elem => elem === upload);
      uploadResolvers.reject(new DOMException('Aborted', 'AbortError'));
    });
    this.uploads.push(upload);
    return upload.promise;
  }

  public cancelUpload(promise: Promise<UploadResponse>): boolean {
    const upload = this.uploads.find(u => u.promise === promise);
    if (upload) {
      upload.abortController.abort();
      return true;
    }
    return false;
  }

  public getCurrentUploads(): Upload[] {
    return this.uploads;
  }

  /**
   * Get the content repository url with query parameters.
   * @returns An object with a 'base', 'path' and 'params' for base URL,
   *          path and query parameters respectively.
   */
  public getContentUri(): IContentUri {
    return {
      base: this.opts.baseUrl,
      path: `${MediaPrefix.V1}/upload`,
      params: {
        access_token: this.opts.accessToken!,
      },
    };
  }
}
